import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { ORDER_STATUSES, lookup } from "@/lib/grocery";
import { ShoppingBag, Package, CheckCircle2, Truck, Receipt as ReceiptIcon, Clock } from "lucide-react";

const STATUS_ICONS = {
  received: Package,
  shopping_in_progress: ShoppingBag,
  receipt_uploaded: ReceiptIcon,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
  cancelled: Clock
};

const StatusBadge = ({ status, lang }) => {
  const s = ORDER_STATUSES.find((x) => x.v === status);
  if (!s) return null;
  return <Badge className={s.color}>{lang === "ar" ? s.ar : s.en}</Badge>;
};

export default function MyOrders() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/orders/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, []); // eslint-disable-line

  return (
    <DashboardLayout title={t("طلباتي", "My Orders")}>
      <div className="space-y-4" data-testid="my-orders-page">
        <div className="glass-card rounded-3xl p-6 bg-luxury relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-yellow-500/20 blur-[100px] rounded-full" />
          <div className="relative flex items-center justify-between flex-wrap gap-3">
            <div>
              <Badge className="bg-yellow-500/20 text-yellow-400 mb-1"><ShoppingBag size={12} className="ml-1" />{t("الطلبات", "Orders")}</Badge>
              <h2 className="text-2xl font-bold text-luxury">{t("تتبّع طلباتك من البقالة", "Track your grocery orders")}</h2>
            </div>
            <Button onClick={() => navigate("/grocery")} variant="outline" className="border-slate-700 text-white">
              {t("تسوّق المزيد", "Shop more")}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="glass-card rounded-2xl h-40 skeleton" />
        ) : orders.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center text-slate-400">
            {t("لا توجد طلبات بعد", "No orders yet")}
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-4">
            {/* List */}
            <div className="space-y-3 lg:col-span-1">
              {orders.map((o) => (
                <button key={o.order_id} onClick={() => setSelected(o)} className={`w-full text-right glass-card rounded-2xl p-4 transition-colors ${selected?.order_id === o.order_id ? "border-yellow-500" : "hover:border-slate-600"}`} data-testid={`order-card-${o.order_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">#{o.order_id.slice(-6)}</span>
                    <StatusBadge status={o.status} lang={language} />
                  </div>
                  <p className="text-white font-semibold text-sm truncate">{o.store_name || t("قائمة تسوق", "Shopping list")}</p>
                  <p className="text-xs text-slate-400 mt-1">{o.items.length} {t("منتجات", "items")} · ${o.total?.toFixed(2)}</p>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="lg:col-span-2">
              {selected ? (
                <div className="glass-card rounded-2xl p-6 space-y-4 purple-gold-glow" data-testid="order-detail">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-white text-lg">#{selected.order_id.slice(-8)}</h3>
                      <p className="text-sm text-slate-400">{selected.store_name || t("قائمة تسوق مخصصة", "Custom shopping list")}</p>
                    </div>
                    <StatusBadge status={selected.status} lang={language} />
                  </div>

                  {/* Status timeline */}
                  <div className="bg-slate-900 rounded-xl p-3 flex justify-between items-center overflow-x-auto">
                    {ORDER_STATUSES.filter((s) => s.v !== "cancelled").map((s, idx) => {
                      const Icon = STATUS_ICONS[s.v] || Clock;
                      const hist = selected.status_history?.find((h) => h.status === s.v);
                      const done = !!hist;
                      const isCurrent = selected.status === s.v;
                      return (
                        <div key={s.v} className="flex flex-col items-center text-center flex-1 min-w-[60px]">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${done || isCurrent ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-500"}`}>
                            <Icon size={16} />
                          </div>
                          <span className={`text-[10px] mt-1 ${done || isCurrent ? "text-yellow-400" : "text-slate-500"}`}>
                            {language === "ar" ? s.ar : s.en}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Items */}
                  <div>
                    <h4 className="font-semibold text-white mb-2 text-sm">{t("المنتجات", "Items")}</h4>
                    <div className="space-y-1.5">
                      {selected.items.map((it, i) => (
                        <div key={i} className="bg-slate-900 rounded-lg p-2 flex justify-between text-sm">
                          <span className="text-white">{it.quantity} {it.unit} · {it.name}</span>
                          {it.price && <span className="text-yellow-400">${(it.price * it.quantity).toFixed(2)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Receipts */}
                  {selected.receipt_images?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-white mb-2 text-sm flex items-center gap-2">
                        <ReceiptIcon size={14} className="text-yellow-500" /> {t("الفواتير", "Receipts")}
                      </h4>
                      <div className="flex gap-2 overflow-x-auto">
                        {selected.receipt_images.map((p, i) => (
                          <div key={i} className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 border border-slate-700">
                            <ListingImage path={p} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Totals + delivery */}
                  <div className="bg-slate-900 rounded-xl p-3 space-y-1 text-sm">
                    <div className="flex justify-between text-slate-400"><span>{t("المجموع", "Subtotal")}</span><span>${selected.subtotal?.toFixed(2)}</span></div>
                    <div className="flex justify-between text-slate-400"><span>{t("التوصيل", "Delivery")}</span><span>${selected.delivery_fee?.toFixed(2)}</span></div>
                    <div className="flex justify-between text-white font-bold pt-1 border-t border-slate-800"><span>{t("الإجمالي", "Total")}</span><span className="text-yellow-400">${selected.total?.toFixed(2)}</span></div>
                  </div>

                  <div className="text-xs text-slate-400">
                    <p>{selected.delivery_address}, {selected.delivery_city}, {selected.delivery_state} {selected.delivery_zip}</p>
                    <p>{t("هاتف:", "Phone:")} {selected.delivery_phone}</p>
                  </div>
                </div>
              ) : (
                <div className="glass-card rounded-2xl p-12 text-center text-slate-400">
                  {t("اختر طلباً لعرض تفاصيله", "Select an order to view details")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
